import {
  DndContext,
  KeyboardSensor,
  MouseSensor,
  TouchSensor,
  closestCenter,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  SortableContext, arrayMove, sortableKeyboardCoordinates,
} from '@dnd-kit/sortable';
import classNames from 'classnames';
import { useFormik } from 'formik';
import PropTypes from 'prop-types';
import React, { createRef, useEffect, useImperativeHandle } from 'react';
import { Modal, ModalBody, ModalHeader } from 'reactstrap';
import * as Yup from 'yup';

import SectionWrapper from 'components/AddLocation/SectionWrapper';
import Button from 'components/App/Button';
import CardImage from 'components/App/CardImage';
import Loading from 'components/App/Loading';

import { ReactComponent as AddPictureIcon } from 'assets/images/add-picture.svg';
import { ReactComponent as NextIconSVG } from 'assets/images/location/next-icon.svg';
import { ReactComponent as PrevIconSVG } from 'assets/images/location/prev-icon.svg';

import { LIMIT_FILE_SIZE } from 'constants/index';
import INITIAL_VALUE from 'constants/initialValue';
import formatUploadImages from 'utils/formatUploadImages';

import useDiscoveryCreateStore from 'store/zustand/useDiscoveryCreateStore';
import styles from './AddDiscoveryPlaceImages.module.scss';

const OPTION_UPLOAD_IMAGES = {
  limitValue: 50,
  fileSize: LIMIT_FILE_SIZE.LARGE,
};

export const placeImagesRef = createRef();

function AddDiscoveryPlaceImages(props) {
  const {
    isLoadingUploadMultipleMedia,
    actionUploadMultiMedia,
    onBack,
    onNext,
  } = props;

  const { formData, setFormData } = useDiscoveryCreateStore();
  const inputFileRef = React.useRef(null);
  const [previewImage, setPreviewImage] = React.useState(null);

  const validation = useFormik({
    validateOnMount: true,
    enableReinitialize: true,
    initialValues: {
      images: Array.isArray(formData.images)
        ? formData.images.map((item, index) => ({
          ...item,
          id: item.id || `img_${index + 1}`,
        }))
        : [],
    },

    validationSchema: Yup.object({
      images: Yup.array()
        .min(3, 'Số lượng ảnh từ 3 đến 50 ảnh')
        .max(50, 'Số lượng ảnh từ 3 đến 50 ảnh')
        .test(
          'checked',
          'Vui lòng chọn ít nhất 3 ảnh',
          (value) => value.filter((item) => item.checked).length >= 3,
        ),
    }),

    onSubmit: (values) => {
      const images = values.images.filter((item) => item.checked);
      if (images.length < 3) {
        validation.setFieldError('images', 'Số lượng ảnh từ 3 đến 50 ảnh');
        return;
      }

      onNext();
      setFormData({ images: values.images });
    },
  });

  // Drop And Drag sensors
  const sensors = useSensors(
    useSensor(MouseSensor, {
      activationConstraint: {
        distance: 10,
      },
    }),
    useSensor(TouchSensor, {
      activationConstraint: {
        delay: 250,
        tolerance: 5,
      },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    }),
  );

  const onDragEnd = React.useCallback((e) => {
    const { active, over } = e;
    if (active && over && active.id !== over.id) {
      const cloneArray = [...validation.values.images];
      const oldIndex = cloneArray.findIndex((pic) => String(pic.id) === String(active.id));
      const newIndex = cloneArray.findIndex((pic) => String(pic.id) === String(over.id));

      if (oldIndex !== -1 && newIndex !== -1) {
        const movedArray = arrayMove(cloneArray, oldIndex, newIndex);
        validation.setFieldValue('images', movedArray, true);
        setFormData({ images: movedArray });
      }
    }
  }, [validation, setFormData]);

  const onUploadMultiImages = React.useCallback((imageList) => {
    const imageUploadList = formatUploadImages(imageList, OPTION_UPLOAD_IMAGES);

    if (imageUploadList.length > 0) {
      actionUploadMultiMedia({
        images: imageUploadList,
        callback: (values) => {
          const timestamp = Date.now();
          const newValues = values.map((item, index) => ({
            image: item,
            id: `img_${timestamp}_${index}`,
            checked: true,
          }));

          const currentImages = validation.values.images || [];
          const updatedList = [...currentImages, ...newValues];

          validation.setFieldValue('images', updatedList, true);
          setFormData({ images: updatedList });

          if (inputFileRef.current) {
            inputFileRef.current.value = '';
          }
        },
      });
    }
  }, [actionUploadMultiMedia, inputFileRef, setFormData, validation]);

  const onChangeAddImages = React.useCallback((e) => {
    if (e.target.files?.length) {
      onUploadMultiImages(e.target.files);
    }
  }, [onUploadMultiImages]);

  const onClickOpenFileInput = React.useCallback(() => {
    if (inputFileRef.current) {
      inputFileRef.current.click();
    }
  }, [inputFileRef]);

  const onSelectAllImages = React.useCallback(() => {
    validation.setFieldValue('images', validation.values.images.map((item) => ({
      ...item,
      checked: true,
    })), true);
  }, [validation]);

  const onClearSelectAllImages = React.useCallback(() => {
    validation.setFieldValue('images', validation.values.images.map((item) => ({
      ...item,
      checked: false,
    })), true);
  }, [validation]);

  const onSelectImage = React.useCallback((e) => {
    const { name } = e.target;
    validation.setFieldValue('images', validation.values.images.map((item) => {
      if (String(item.id) === String(name)) {
        return {
          ...item,
          checked: !item.checked,
        };
      }
      return item;
    }), true);
  }, [validation]);

  const onPreviewImage = React.useCallback((image) => {
    setPreviewImage(image);
  }, []);

  const onDeleteSelectedImages = React.useCallback(() => {
    const filterImages = validation.values.images.filter((item) => item.checked !== true);
    validation.setFieldValue('images', filterImages, true);
    setFormData({ images: filterImages });
  }, [validation, setFormData]);

  useEffect(() => {
    setFormData({ isImagesValid: validation.isValid });
  }, [setFormData, validation.isValid]);

  useImperativeHandle(placeImagesRef, () => ({
    saveData: () => setFormData(validation.values),
  }));

  return (
    <form className={styles.Component} onSubmit={validation.handleSubmit}>
      <Loading loading={isLoadingUploadMultipleMedia} loaderOnPage />

      <SectionWrapper title="Hình ảnh điểm khám phá" className={styles.SectionWrapper}>
        {validation.values.images.length > 0 && (
          <div className={styles.ButtonWrap}>
            <Button
              isDefault
              classes={styles.ButtonCheckAll}
              onClick={onSelectAllImages}
            >
              Chọn tất cả
            </Button>

            <Button
              isDefault
              classes={styles.ButtonUnCheck}
              onClick={onClearSelectAllImages}
              disabled={!validation.values.images.some((item) => item.checked)}
            >
              Bỏ chọn tất cả
            </Button>

            <Button
              isDefault
              classes={styles.ButtonRemove}
              onClick={onDeleteSelectedImages}
              disabled={!validation.values.images.some((item) => item.checked)}
            >
              Xóa
            </Button>
          </div>
        )}

        <div className={styles.ImageList}>
          <DndContext
            sensors={sensors}
            collisionDetection={closestCenter}
            onDragEnd={onDragEnd}
          >
            <SortableContext
              items={validation.values.images.map((picture) => picture.id)}
            >
              {validation.values.images.map((picture) => (
                <CardImage
                  key={picture.id}
                  item={picture}
                  onSelectImage={onSelectImage}
                  onPreviewImage={onPreviewImage}
                />
              ))}

              <Button
                isDefault
                type="button"
                onClick={onClickOpenFileInput}
                classes={classNames(
                  styles.UploadImage,
                  validation.values.images.length === 0 && styles.UploadImageFull,
                )}
              >
                <AddPictureIcon />
                <span>THÊM ẢNH</span>
              </Button>

              <input
                ref={inputFileRef}
                type="file"
                accept="image/jpeg,image/jpg,image/png"
                multiple
                style={{ display: 'none' }}
                onChange={onChangeAddImages}
              />
            </SortableContext>
          </DndContext>
        </div>

        {validation.errors.images && validation.touched.images && (
          <p className={styles.Error}>{validation.errors.images}</p>
        )}

        <span className={styles.DescriptionTitle}>Tải ít nhất 3 hình lên</span>

      </SectionWrapper>

      <Modal isOpen={!!previewImage} toggle={() => setPreviewImage(null)} size="lg" centered>
        <ModalHeader toggle={() => setPreviewImage(null)} className="border-0 pb-0" />
        <ModalBody className="text-center p-3 overflow-hidden d-flex justify-content-center align-items-center">
          <img
            src={previewImage}
            alt="Preview"
            style={{
              maxWidth: '100%',
              maxHeight: '75vh',
              objectFit: 'contain',
              borderRadius: '4px',
            }}
          />
        </ModalBody>
      </Modal>

      <div className={styles.FormAction}>
        <Button
          classes="Btn Btn__PrevStep"
          outline
          onClick={onBack}
        >
          <PrevIconSVG className="Icon__PrevStep" />
          Quay về bước trước
        </Button>

        <Button
          classes="Btn Btn__NextStep"
          type="submit"
        >
          Bước tiếp theo
          <NextIconSVG className="Icon__NextStep" />
        </Button>
      </div>
    </form>
  );
}

AddDiscoveryPlaceImages.propTypes = {
  isLoadingUploadMultipleMedia: PropTypes.bool,

  actionUploadMultiMedia: PropTypes.func,
  onBack: PropTypes.func,

  onNext: PropTypes.func.isRequired,
};

AddDiscoveryPlaceImages.defaultProps = {
  isLoadingUploadMultipleMedia: false,

  actionUploadMultiMedia: INITIAL_VALUE.FUNCTION,
  onBack: INITIAL_VALUE.FUNCTION,
};

export default React.memo(AddDiscoveryPlaceImages);
